import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger,
  MessageBar, MessageBarBody, Radio, RadioGroup, Select, Spinner, Text, Textarea, makeStyles, shorthands, tokens,
} from '@fluentui/react-components';
import {
  AddRegular, ArrowDownloadRegular, DocumentPdfRegular, DocumentRegular, DocumentTableRegular, DocumentTextRegular, FolderZipRegular, MoreHorizontalRegular, SearchRegular, SlideTextRegular,
} from '@fluentui/react-icons';
import {
  KB_CATEGORIES, KB_CATEGORY_LABELS, KB_FILE_TYPES, KB_FILE_TYPE_LABELS, KB_MAX_BYTES, KB_VISIBILITIES, KB_VISIBILITY_LABELS, type KbCategory, type KbDocumentView, type KbFileType, type KbVersionView, type KbVisibility,
} from '@vxh/shared';
import { api, apiDownload, apiUpload } from '../api';
import { useAuth } from '../auth';
import { PageHeader } from '../components/PageHeader';
import { ago, formatBytes, fullTime, nextVersion } from '../format';

const useStyles = makeStyles({
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('14px') },
  search: { width: '100%', maxWidth: '420px', marginBottom: '20px' },
  group: { marginBottom: '28px' },
  list: { display: 'grid', ...shorthands.gap('8px'), marginTop: '8px' },
  row: {
    display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr) auto', alignItems: 'center', ...shorthands.gap('14px'), ...shorthands.padding('14px', '16px'),
    backgroundColor: tokens.colorNeutralBackground1, ...shorthands.borderRadius('6px'), ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    '@media (max-width: 720px)': { gridTemplateColumns: '36px minmax(0, 1fr)' },
  },
  icon: { fontSize: '28px', color: '#4657D2', display: 'flex' },
  body: { display: 'grid', ...shorthands.gap('4px'), minWidth: 0 },
  meta: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', ...shorthands.gap('6px', '10px'), color: tokens.colorNeutralForeground3, fontSize: '12px' },
  actions: { display: 'flex', alignItems: 'center', ...shorthands.gap('6px'), '@media (max-width: 720px)': { gridColumn: '2' } },
  muted: { color: tokens.colorNeutralForeground3 },
  empty: { textAlign: 'center', ...shorthands.padding('48px', '16px'), color: tokens.colorNeutralForeground3 },
});

const ICONS: Record<KbFileType, JSX.Element> = {
  docx: <DocumentRegular />, pdf: <DocumentPdfRegular />, pptx: <SlideTextRegular />, xlsx: <DocumentTableRegular />, md: <DocumentTextRegular />, zip: <FolderZipRegular />,
};
const ACCEPT = KB_FILE_TYPES.map((t) => `.${t}`).join(',');
const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/** Customer-ready documents, grouped by category. Everyone with a hub account sees what is meant for them; administrators also manage the library. */
export function KnowledgePage() {
  const s = useStyles();
  const { isAdmin, user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['knowledge'], queryFn: () => api<KbDocumentView[]>('/knowledge') });
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<KbDocumentView | null>(null);
  const [replacing, setReplacing] = useState<KbDocumentView | null>(null);
  const [history, setHistory] = useState<KbDocumentView | null>(null);
  const [deleting, setDeleting] = useState<KbDocumentView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['knowledge'] });
  const isStaff = user?.kind === 'staff';

  const download = async (d: KbDocumentView) => {
    setBusy(d.id);
    setError(null);
    try {
      await apiDownload(`/knowledge/${d.id}/download`, d.downloadName);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const groups = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const docs = (q.data ?? []).filter((d) => {
      const hay = `${d.title} ${d.description} ${KB_CATEGORY_LABELS[d.category]} ${KB_FILE_TYPE_LABELS[d.fileType]} ${d.fileType} ${d.version}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
    return KB_CATEGORIES.map((c) => ({ category: c, docs: docs.filter((d) => d.category === c) })).filter((g) => g.docs.length > 0);
  }, [q.data, search]);

  return (
    <>
      <PageHeader
        title="Knowledge base"
        subtitle="Guides, manuals and security documents to download and share. Each one is the current version."
        actions={isAdmin ? <Button appearance="primary" icon={<AddRegular />} onClick={() => setAdding(true)}>Add a document</Button> : undefined}
      />
      {error && <MessageBar intent="error" style={{ marginBottom: 12 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {notice && <MessageBar intent="success" style={{ marginBottom: 12 }}><MessageBarBody>{notice}</MessageBarBody></MessageBar>}
      <Input className={s.search} contentBefore={<SearchRegular />} placeholder="Search by title, topic or file type" value={search} onChange={(_, d) => setSearch(d.value)} aria-label="Search the Knowledge base" />
      {q.isLoading ? <Spinner label="Loading" /> : q.isError ? (
        <MessageBar intent="error"><MessageBarBody>{(q.error as Error).message}</MessageBarBody></MessageBar>
      ) : (q.data ?? []).length === 0 ? (
        <div className={s.empty}>
          <Text size={400} weight="semibold" block>No documents yet</Text>
          <Text>{isAdmin ? 'Choose "Add a document" to put the first guide here.' : 'Documents will appear here when Voxshift adds them.'}</Text>
        </div>
      ) : groups.length === 0 ? (
        <div className={s.empty}><Text>Nothing matches "{search}". Try a shorter word.</Text></div>
      ) : groups.map((g) => (
        <section key={g.category} className={s.group} aria-label={KB_CATEGORY_LABELS[g.category]}>
          <Text as="h2" size={500} weight="semibold" block>{KB_CATEGORY_LABELS[g.category]}</Text>
          <div className={s.list}>
            {g.docs.map((d) => (
              <div key={d.id} className={s.row}>
                <span className={s.icon} aria-hidden>{ICONS[d.fileType]}</span>
                <div className={s.body}>
                  <Text weight="semibold">{d.title}</Text>
                  {d.description && <Text className={s.muted}>{d.description}</Text>}
                  <div className={s.meta}>
                    <span>{KB_FILE_TYPE_LABELS[d.fileType]} · {formatBytes(d.sizeBytes)}</span>
                    <span>Version {d.version}</span>
                    <span title={fullTime(d.updatedAt)}>Updated {date(d.updatedAt)}</span>
                    {isStaff && d.visibility === 'staff' && <Badge appearance="tint" color="warning" shape="rounded">Voxshift staff only: do not send to customers</Badge>}
                  </div>
                </div>
                <div className={s.actions}>
                  <Button appearance="primary" icon={busy === d.id ? <Spinner size="tiny" /> : <ArrowDownloadRegular />} disabled={busy === d.id} onClick={() => void download(d)} aria-label={`Download ${d.title}`}>Download</Button>
                  {isAdmin && (
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button appearance="subtle" icon={<MoreHorizontalRegular />} aria-label={`More actions for ${d.title}`} />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem onClick={() => setEditing(d)}>Edit details</MenuItem>
                          <MenuItem onClick={() => setReplacing(d)}>Upload a new version</MenuItem>
                          <MenuItem disabled={(d.versionCount ?? 1) <= 1} onClick={() => setHistory(d)}>{(d.versionCount ?? 1) > 1 ? `Earlier versions (${(d.versionCount ?? 1) - 1})` : 'Earlier versions (none yet)'}</MenuItem>
                          <MenuItem onClick={() => setDeleting(d)}>Delete</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
      {adding && <AddDialog onClose={() => setAdding(false)} onDone={(t) => (setAdding(false), setNotice(`"${t}" was added.`), void refresh())} />}
      {editing && <EditDialog doc={editing} onClose={() => setEditing(null)} onDone={() => (setEditing(null), setNotice('Saved.'), void refresh())} />}
      {replacing && <ReplaceDialog doc={replacing} onClose={() => setReplacing(null)} onDone={() => (setReplacing(null), setNotice(`The new version of "${replacing.title}" is live. The previous one is kept under Earlier versions.`), void refresh())} />}
      {history && <HistoryDialog doc={history} onClose={() => setHistory(null)} onError={setError} />}
      {deleting && <DeleteDialog doc={deleting} onClose={() => setDeleting(null)} onDone={() => (setDeleting(null), setNotice(`"${deleting.title}" was deleted.`), void refresh())} />}
    </>
  );
}

/** A native file chooser (Fluent has none), with the size and type rules said in plain words. */
function FilePicker({ file, onPick }: { file: File | null; onPick: (f: File | null) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const tooBig = !!file && file.size > KB_MAX_BYTES;
  return (
    <Field label="File" required hint={`Word, PDF, PowerPoint, Excel, Markdown or Zip, up to ${KB_MAX_BYTES / 1024 / 1024} MB.`} validationState={tooBig ? 'error' : 'none'} validationMessage={tooBig ? 'That file is larger than 25 MB.' : undefined}>
      <input ref={ref} type="file" accept={ACCEPT} onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
    </Field>
  );
}
const fileOk = (f: File | null) => !!f && f.size > 0 && f.size <= KB_MAX_BYTES;

function VisibilityField({ value, onChange }: { value: KbVisibility; onChange: (v: KbVisibility) => void }) {
  return (
    <Field label="Who can see it" required>
      <RadioGroup value={value} onChange={(_, d) => onChange(d.value as KbVisibility)}>
        {KB_VISIBILITIES.map((v) => <Radio key={v} value={v} label={KB_VISIBILITY_LABELS[v]} />)}
      </RadioGroup>
    </Field>
  );
}

function DetailsFields(p: { title: string; description: string; category: KbCategory; visibility: KbVisibility; version: string; set: (k: 'title' | 'description' | 'category' | 'visibility' | 'version', v: string) => void }) {
  return (
    <>
      <Field label="Title" required><Input value={p.title} onChange={(_, d) => p.set('title', d.value)} maxLength={120} /></Field>
      <Field label="Short description" hint="One or two sentences on what it is for.">
        <Textarea value={p.description} onChange={(_, d) => p.set('description', d.value)} maxLength={400} rows={2} />
      </Field>
      <Field label="Category">
        <Select value={p.category} onChange={(_, d) => p.set('category', d.value)}>
          {KB_CATEGORIES.map((c) => <option key={c} value={c}>{KB_CATEGORY_LABELS[c]}</option>)}
        </Select>
      </Field>
      <Field label="Version" hint="For example 1.0 or 2026.10. Shown next to the document.">
        <Input value={p.version} onChange={(_, d) => p.set('version', d.value)} maxLength={20} style={{ maxWidth: 160 }} />
      </Field>
      <VisibilityField value={p.visibility} onChange={(v) => p.set('visibility', v)} />
    </>
  );
}

function AddDialog({ onClose, onDone }: { onClose: () => void; onDone: (title: string) => void }) {
  const s = useStyles();
  const [file, setFile] = useState<File | null>(null);
  const [f, setF] = useState({ title: '', description: '', category: 'installation' as KbCategory, visibility: 'staff' as KbVisibility, version: '1.0' });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => apiUpload<KbDocumentView>(`/knowledge?${new URLSearchParams({ ...f, filename: file!.name })}`, file!, 'POST'),
    onSuccess: (d) => onDone(d.title),
    onError: (e) => setError((e as Error).message),
  });
  // A name for the title from the file name, so the common case is one click less.
  const pick = (picked: File | null) => {
    setFile(picked);
    if (picked && !f.title) setF({ ...f, title: picked.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ') });
  };
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && !save.isPending && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add a document</DialogTitle>
          <DialogContent className={s.grid}>
            <FilePicker file={file} onPick={pick} />
            <DetailsFields {...f} set={(k, v) => setF({ ...f, [k]: v })} />
            {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={save.isPending}>Cancel</Button>
            <Button appearance="primary" disabled={!fileOk(file) || !f.title.trim() || !f.version.trim() || save.isPending} onClick={() => (setError(null), save.mutate())}>{save.isPending ? 'Uploading…' : 'Add document'}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function EditDialog({ doc, onClose, onDone }: { doc: KbDocumentView; onClose: () => void; onDone: () => void }) {
  const s = useStyles();
  const [f, setF] = useState({ title: doc.title, description: doc.description, category: doc.category, visibility: doc.visibility, version: doc.version });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api(`/knowledge/${doc.id}`, { method: 'PATCH', body: f }),
    onSuccess: onDone,
    onError: (e) => setError((e as Error).message),
  });
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Edit details</DialogTitle>
          <DialogContent className={s.grid}>
            <DetailsFields {...f} set={(k, v) => setF({ ...f, [k]: v })} />
            <Text className={s.muted}>The file itself is not changed. To replace it, use "Upload a new version".</Text>
            {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Cancel</Button>
            <Button appearance="primary" disabled={!f.title.trim() || !f.version.trim() || save.isPending} onClick={() => (setError(null), save.mutate())}>Save</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function ReplaceDialog({ doc, onClose, onDone }: { doc: KbDocumentView; onClose: () => void; onDone: () => void }) {
  const s = useStyles();
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState(nextVersion(doc.version));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => apiUpload(`/knowledge/${doc.id}/file?${new URLSearchParams({ version, filename: file!.name })}`, file!, 'PUT'),
    onSuccess: onDone,
    onError: (e) => setError((e as Error).message),
  });
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && !save.isPending && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Upload a new version of "{doc.title}"</DialogTitle>
          <DialogContent className={s.grid}>
            <Text>People will download the new file from now on. The current version ({doc.version}) is kept, and you can still download it under "Earlier versions".</Text>
            <FilePicker file={file} onPick={setFile} />
            <Field label="New version" required hint={`The current version is ${doc.version}.`}>
              <Input value={version} onChange={(_, d) => setVersion(d.value)} maxLength={20} style={{ maxWidth: 160 }} />
            </Field>
            {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={save.isPending}>Cancel</Button>
            <Button appearance="primary" disabled={!fileOk(file) || !version.trim() || save.isPending} onClick={() => (setError(null), save.mutate())}>{save.isPending ? 'Uploading…' : 'Upload new version'}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function HistoryDialog({ doc, onClose, onError }: { doc: KbDocumentView; onClose: () => void; onError: (m: string | null) => void }) {
  const s = useStyles();
  const q = useQuery({ queryKey: ['knowledge-versions', doc.id, doc.version], queryFn: () => api<KbVersionView[]>(`/knowledge/${doc.id}/versions`) });
  const get = async (v: KbVersionView) => {
    try {
      await apiDownload(`/knowledge/${doc.id}/versions/${v.id}/download`, v.downloadName);
    } catch (e) {
      onError((e as Error).message);
      onClose();
    }
  };
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Versions of "{doc.title}"</DialogTitle>
          <DialogContent className={s.grid}>
            {q.isLoading ? <Spinner label="Loading" /> : q.isError ? <MessageBar intent="error"><MessageBarBody>{(q.error as Error).message}</MessageBarBody></MessageBar> : (q.data ?? []).map((v) => (
              <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <Text weight="semibold">Version {v.version}</Text> {v.current && <Badge appearance="tint" color="success" shape="rounded">Current</Badge>}
                  <div className={s.muted}>{ago(v.uploadedAt)} · {v.uploadedBy ?? 'a removed account'} · {formatBytes(v.sizeBytes)} · {v.originalName}</div>
                </div>
                <Button size="small" icon={<ArrowDownloadRegular />} onClick={() => void get(v)} aria-label={`Download version ${v.version}`}>Download</Button>
              </div>
            ))}
            <Text className={s.muted}>Only administrators can download earlier versions.</Text>
          </DialogContent>
          <DialogActions><Button appearance="primary" onClick={onClose}>Close</Button></DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function DeleteDialog({ doc, onClose, onDone }: { doc: KbDocumentView; onClose: () => void; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: () => api(`/knowledge/${doc.id}`, { method: 'DELETE' }),
    onSuccess: onDone,
    onError: (e) => setError((e as Error).message),
  });
  const n = doc.versionCount ?? 1;
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Delete "{doc.title}"?</DialogTitle>
          <DialogContent>
            <Text>It disappears for everyone straight away{n > 1 ? `, together with its ${n - 1} earlier version${n > 2 ? 's' : ''}` : ''}. This cannot be undone. Keep your own copy if you may need it again.</Text>
            {error && <MessageBar intent="error" style={{ marginTop: 12 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Keep it</Button>
            <Button appearance="primary" disabled={del.isPending} onClick={() => del.mutate()} style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}>Delete document</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
